import React from "react";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#d97706";

export default function Finance() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>💰</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>Finance</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Finance</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 640, lineHeight: 1.6 }}>
          Client billing, Thai-compliant sales documents, vendor bills, AI-powered expense capture, and payroll.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "invoices", label: "Invoices & Quotations" },
        { id: "thai-docs", label: "Thai Sales Documents" },
        { id: "vendors", label: "Vendors & Vendor Bills" },
        { id: "expense-capture", label: "AI Expense Capture" },
        { id: "expense-claims", label: "Expense Claims" },
        { id: "payroll", label: "Payroll & Salary Slips" },
      ]} />

      <Section id="invoices" title="Invoices & Quotations">
        <AvailableTo roles={["admin", "finance"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Create, manage, and download client invoices. Supports multiple currencies, tax (GST/VAT), TDS, and
          auto-numbered invoice IDs.
        </p>
        <Img src="/docs/screenshots/finance-invoices.png" alt="Invoices list" />
        <Steps items={[
          "Click Invoices in the sidebar.",
          "Click New Invoice.",
          "Select the client, currency, and tax settings (tax %, TDS %).",
          "Add line items — description, quantity, unit price. The totals calculate automatically.",
          "Set the invoice date and due date.",
          "Save as Draft or mark as Sent.",
          "Download PDF: click Download on any invoice to get a branded PDF.",
          "Mark Paid: once payment is received, update the status to Paid.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Invoice numbering"
          body="Invoice numbers are auto-generated (e.g. INV-2026-0001) and are unique per company. You cannot manually set the number."
        />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, margin: "24px 0 16px" }}>
          Quotations use the same form as invoices — send a non-binding estimate to a prospect, then convert
          it to an invoice in one click once they accept.
        </p>
        <Steps items={[
          "Click Invoices in the sidebar → switch to the Quotations tab.",
          "Click New Quotation — the form is identical to an invoice (client, currency, line items, tax).",
          "Save and send to the client as PDF.",
          "When the client accepts: click Convert to Invoice — a new invoice is created pre-filled from the quotation.",
        ]} />
        <Callout
          icon="⚠️"
          title="Overdue invoices"
          body="The sidebar shows a red badge with the count of overdue invoices. An invoice is overdue if the due date has passed and it's not marked Paid."
        />
      </Section>

      <Section id="thai-docs" title="Thai Sales Documents (Billing Note → Tax Invoice → Receipt)">
        <AvailableTo roles={["admin", "finance"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Sales documents flow through a chain — each stage converts to the next
          with one click, and the original document is preserved.
        </p>
        <Steps items={[
          "Quotation — convert to a Billing Note or straight to a Tax Invoice.",
          "Billing Note — convert to a Tax Invoice or Receipt.",
          "Tax Invoice — mark it Paid to automatically generate a Receipt.",
          "Receipt — the final proof-of-payment document sent to the client.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Credit and debit notes"
          body="Sales Credit/Debit Notes adjust an existing Tax Invoice — use them for refunds, discounts, or corrections after a Tax Invoice has been issued."
        />
      </Section>

      <Section id="vendors" title="Vendors & Vendor Bills">
        <AvailableTo roles={["admin", "finance"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Track money owed to your suppliers, separately from client billing.
        </p>
        <Img src="/docs/screenshots/finance-vendor-bills.png" alt="Vendor Bills page" />
        <Steps items={[
          "Click Vendors in the sidebar to manage your supplier list.",
          "Click Vendor Bills to record a bill received from a vendor — vendor, bill date, due date, and amount.",
          "Fields marked with an AI badge were pre-filled automatically from an uploaded bill document — review them before saving.",
          "Track status: Draft → Submitted → Paid, or Void. Overdue bills (past due date, not yet paid) are flagged in the list.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Debit/Credit Notes (vendor side)"
          body="Debit/Credit Notes adjust an existing Vendor Bill — this is the purchase-side equivalent of the Sales Credit/Debit Notes used against Tax Invoices."
        />
      </Section>

      <Section id="expense-capture" title="AI Expense Capture">
        <AvailableTo roles={["admin", "finance"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Snap or upload a receipt and the AI extracts the vendor, date, amount, and line items for you to
          review instead of typing them in manually.
        </p>
        <Img src="/docs/screenshots/finance-ai-expense-capture.png" alt="AI Expense Capture page" />
        <Steps items={[
          "Click AI Expense Capture in the sidebar.",
          "Capture tab: upload a photo or PDF of a receipt (or use Bulk Receipt Upload to drag-and-drop several at once).",
          "The AI reads the receipt and sets a status: Ready to Confirm (high confidence) or Needs Review (something is unclear or doesn't reconcile).",
          "Confirm the extracted details, or edit any field the AI got wrong, then save.",
          "Ledger tab: see every captured expense in one place, filterable by status.",
          "Insights tab: spending trends and vendor breakdowns drawn from your captured expenses.",
        ]} />
        <Callout
          icon="🤖"
          title="Auto-confirm and delivery notes"
          body="A capture is auto-confirmed only when every field is high-confidence, the math reconciles, and the vendor is already known — otherwise it waits for your review. Delivery notes are detected and linked to the vendor for reference, but are never booked as an expense."
        />
      </Section>

      <Section id="expense-claims" title="Expense Claims">
        <AvailableTo roles={["admin", "admin_assistant", "hr", "recruiter", "finance", "employee"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Any employee can submit an expense claim for reimbursement; Admin and Admin Assistant approve them.
        </p>
        <Img src="/docs/screenshots/employee-expenses.png" alt="Expenses page" />
        <Steps items={[
          "Submitting: click Expenses in the sidebar, then New Expense — fill in the title, amount, category, date, and upload a receipt.",
          "Approving: the approver's Expenses page has a Pending tab — review category, amount, and receipt, then Approve or Reject.",
          "Once approved, Finance marks it Paid from the Approved tab.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Expense statuses"
          body="Pending → Approved → Paid, or Rejected. The employee gets an email at each step."
        />
      </Section>

      <Section id="payroll" title="Payroll & Salary Slips">
        <AvailableTo roles={["admin", "admin_assistant"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Generate monthly salary slips for all employees. Run payroll in bulk or generate individual slips.
        </p>
        <Img src="/docs/screenshots/aa-payroll.png" alt="Payroll page" />
        <Steps items={[
          "Click Payroll in the sidebar.",
          "Select the month and year.",
          "Click Run Payroll — this generates salary slips for all active employees.",
          "Individual slips can be edited (earnings/deductions) before generating.",
          "Click Issue to send the payslip to the employee. They receive an email notification.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Earnings & deductions"
          body="Salary structures (base pay, allowances, deductions) are set on each employee's profile. Payroll uses those values when generating slips."
        />
        <div style={{ marginTop: 24 }}>
          <AvailableTo roles={["admin", "admin_assistant", "hr", "recruiter", "finance", "employee"]} />
        </div>
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Every employee can view and download their own issued payslips.
        </p>
        <Img src="/docs/screenshots/employee-slips.png" alt="Salary Slips page" />
        <Steps items={[
          "Click Salary Slips in the sidebar.",
          "The list shows all payslips issued to you, sorted by most recent.",
          "Click Download PDF on any payslip to save it.",
        ]} />
      </Section>
    </DocsLayout>
  );
}
